import React, { ElementType, useEffect, useState } from 'react';
import {
  Box,
  Button,
  HStack,
  Image,
  SimpleGrid,
  Stack,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { DeleteIcon, EditIcon } from '@chakra-ui/icons';
import { FiImage } from 'react-icons/fi';
import { CatalogueItem } from '../../utils/types';
import {
  deleteCatalogueItem,
  getCatalogueItem,
} from '../../hooks/catalogueItems';
import ConfirmModal from '../../components/ConfirmationModal';
import {
  CatalogueField,
  CatalogueHero,
  CatalogueListSkeleton,
  CatalogueSectionCard,
  CatalogueShell,
  CatalogueStatusBadge,
  PrimaryCatalogueButton,
  formatCurrency,
  formatDateLabel,
} from './components/catalogueUi';

const CatalogueItemDetailsPage = () => {
  const { orgId, itemId, catalogueId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [product, setProduct] = useState<CatalogueItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadAttempted, setLoadAttempted] = useState(false);

  const fetchProduct = async () => {
    if (!orgId || !catalogueId || !itemId) {
      return;
    }

    setLoading(true);
    const result = await getCatalogueItem(orgId, catalogueId, itemId);
    setLoading(false);
    setLoadAttempted(true);

    if (!result.success || !result.data) {
      toast({
        title: 'Unable to load item',
        description: 'The product details could not be retrieved.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setProduct(result.data);
  };

  useEffect(() => {
    fetchProduct();
  }, [orgId, catalogueId, itemId]);

  const handleDelete = async () => {
    if (!orgId || !catalogueId || !itemId || !product) {
      return;
    }

    const response = await deleteCatalogueItem(orgId, itemId, catalogueId);

    if (!response.success) {
      toast({
        title: 'Delete failed',
        description: 'We could not delete this item.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    toast({
      title: 'Item deleted',
      description: `${product.name} was removed from the catalogue.`,
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
    navigate(`/organization/${orgId}/catalogue`);
  };

  if (loading || !product) {
    if (loadAttempted && !product) {
      return (
        <CatalogueShell compact>
          <CatalogueSectionCard
            title="Item unavailable"
            description="The catalogue item could not be loaded. Go back to the catalogue and try again."
          >
            <Button borderRadius="16px" onClick={() => navigate(`/organization/${orgId}/catalogue`)}>
              Back to catalogue
            </Button>
          </CatalogueSectionCard>
        </CatalogueShell>
      );
    }

    return (
      <CatalogueShell compact>
        <CatalogueListSkeleton />
      </CatalogueShell>
    );
  }

  return (
    <CatalogueShell compact>
      <CatalogueHero
        eyebrow="Item details"
        title={product.name}
        description="Review product details, confirm media quality, and move into editing or image management without hunting through the catalogue."
        primaryAction={
          <PrimaryCatalogueButton
            leftIcon={<EditIcon />}
            onClick={() =>
              navigate(
                `/organization/${orgId}/catalogue/${catalogueId}/catalogue-item/${product.id}/edit`,
              )
            }
          >
            Edit item
          </PrimaryCatalogueButton>
        }
        secondaryAction={
          <Button
            borderRadius="16px"
            variant="outline"
            leftIcon={<Box as={FiImage as ElementType} />}
            onClick={() =>
              navigate(
                `/organization/${orgId}/catalogue/${catalogueId}/catalogue-item/${product.id}/images`,
              )
            }
          >
            Manage images
          </Button>
        }
      />

      <SimpleGrid columns={{ base: 1, xl: 3 }} spacing={6} alignItems="start">
        <Stack spacing={6} gridColumn={{ xl: 'span 2' }}>
          <CatalogueSectionCard
            title="Media"
            description="A clear visual read of the product. The first image acts as the default cover across the catalogue."
          >
            {product.images?.length ? (
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                {product.images.map((image, index) => (
                  <Box
                    key={image.id}
                    borderRadius="22px"
                    overflow="hidden"
                    borderWidth="1px"
                    borderColor="gray.100"
                    bg="gray.50"
                  >
                    <Box h={{ base: '240px', md: '280px' }}>
                      <Image
                        src={image.image}
                        alt={`${product.name} ${index + 1}`}
                        w="full"
                        h="full"
                        objectFit="cover"
                      />
                    </Box>
                    <HStack justify="space-between" px={4} py={3}>
                      <Text fontWeight="700" color="gray.900">
                        {index === 0 ? 'Cover image' : `Image ${index + 1}`}
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        {formatDateLabel(image.uploaded_at)}
                      </Text>
                    </HStack>
                  </Box>
                ))}
              </SimpleGrid>
            ) : (
              <VStack
                py={12}
                px={6}
                borderWidth="1px"
                borderColor="gray.100"
                borderRadius="24px"
                bg="gray.50"
                spacing={3}
              >
                <Box as={FiImage as ElementType} boxSize={8} color="gray.400" />
                <Text fontWeight="700" color="gray.900">
                  No images uploaded yet
                </Text>
                <Text color="gray.500" fontSize="sm" textAlign="center" maxW="md">
                  Add images to increase trust and make browsing easier on smaller screens.
                </Text>
                <Button
                  borderRadius="16px"
                  onClick={() =>
                    navigate(
                      `/organization/${orgId}/catalogue/${catalogueId}/catalogue-item/${product.id}/images`,
                    )
                  }
                >
                  Add images
                </Button>
              </VStack>
            )}
          </CatalogueSectionCard>

          <CatalogueSectionCard
            title="Description"
            description="A clean summary for sales, customer support, and operations review."
          >
            <Text color="gray.700" whiteSpace="pre-wrap">
              {product.description || 'No description provided yet.'}
            </Text>
          </CatalogueSectionCard>
        </Stack>

        <Stack spacing={6}>
          <CatalogueSectionCard
            title="Overview"
            description="Quick operational context without switching into edit mode."
          >
            <Stack spacing={4}>
              <CatalogueField
                label="Price"
                value={formatCurrency(product.price)}
              />
              <CatalogueField
                label="Status"
                value={
                  <CatalogueStatusBadge
                    isActive={Boolean(product.is_active)}
                    imageCount={product.images?.length}
                  />
                }
              />
              <CatalogueField
                label="Media count"
                value={`${product.images?.length || 0} image${
                  (product.images?.length || 0) === 1 ? '' : 's'
                }`}
              />
              <CatalogueField
                label="Last updated"
                value={formatDateLabel(product.updated_at || product.created_at)}
              />
            </Stack>
          </CatalogueSectionCard>

          <CatalogueSectionCard
            title="Actions"
            description="Keep the next steps obvious and safe."
          >
            <VStack align="stretch" spacing={3}>
              <PrimaryCatalogueButton
                leftIcon={<EditIcon />}
                onClick={() =>
                  navigate(
                    `/organization/${orgId}/catalogue/${catalogueId}/catalogue-item/${product.id}/edit`,
                  )
                }
              >
                Edit item
              </PrimaryCatalogueButton>
              <Button
                borderRadius="16px"
                variant="outline"
                leftIcon={<Box as={FiImage as ElementType} />}
                onClick={() =>
                  navigate(
                    `/organization/${orgId}/catalogue/${catalogueId}/catalogue-item/${product.id}/images`,
                  )
                }
              >
                Manage images
              </Button>
              <Button
                borderRadius="16px"
                colorScheme="red"
                variant="ghost"
                leftIcon={<DeleteIcon />}
                onClick={onOpen}
              >
                Delete item
              </Button>
            </VStack>
          </CatalogueSectionCard>
        </Stack>
      </SimpleGrid>

      <ConfirmModal
        isOpen={isOpen}
        onClose={onClose}
        handleConfirm={handleDelete}
        isLoading={false}
        AreYouSureQuestion={`Delete ${product.name}?`}
        AreYouSureTextExplanation="This action permanently removes the item from the catalogue."
        confirmButtonText="Delete item"
        confirmButtonColor="#E53E3E"
        colorScheme="red"
      />
    </CatalogueShell>
  );
};

export default CatalogueItemDetailsPage;
